import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";
import { createSelectorCreator, defaultMemoize } from "reselect";

import { Account, OkResponse, Project, ProjectPreview, ProjectSubtype, ProjectType, Store } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { searchCollectionByKey } from "fond/utils/search";

import { selectAccountById, selectCurrentAccount } from "./accountSlice";
import { apiSlice } from "./apiSlice";
import { selectWorkflowEntities } from "./workflowSlice";

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export type GetProjectsResponse = {
  // FIXME: FND-3829 This should be 'Items: ProjectPreview[]' but left as Project[] for now.
  Items: Project[];
};

export const projectsAdapter = createEntityAdapter<Project>({
  selectId: (entity: Project): string => entity.ID,
});
const projectsInitialState = projectsAdapter.getInitialState();

/**
 * Projects API Slice
 */
export const projectSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getProject: build.query<Project, string>({
      query: (projectId: string) => `/v2/projects/${projectId}`,
      providesTags: (result) => (result ? [{ type: "Project", id: result.ID }] : []),
      transformResponse: (response: Project): Project => ({
        ...response,
        EntityType: "project",
      }),
    }),
    getProjects: build.query<ReturnType<typeof projectsAdapter.setAll>, { accountId?: string } | void>({
      query: (params) => ({
        url: "/v2/projects",
        params: { account_id: params?.accountId },
      }),
      transformResponse: (response: GetProjectsResponse) => {
        return projectsAdapter.setAll(
          projectsInitialState,
          response.Items.map((project) => ({ ...project, EntityType: "project" }))
        );
      },
      providesTags: (result, _error, params) => {
        const accountListId = `LIST:${params?.accountId || "all"}`;
        const listTags = [
          { type: "Project" as const, id: "LIST" },
          { type: "Project" as const, id: accountListId },
        ];
        return result ? [...result.ids.map((id) => ({ type: "Project" as const, id })), ...listTags] : listTags;
      },
    }),
    copyProject: build.mutation<Project, { projectId: string; folderId: string | null }>({
      query: ({ projectId, folderId }) => ({
        url: `/v2/projects/${projectId}/copy`,
        method: "POST",
        body: { FolderID: folderId },
      }),
      invalidatesTags: (result, _error, _arg) => (result ? [{ type: "Project", id: `LIST:${result.Account.ID}` }] : []),
    }),
    createProject: build.mutation<
      Project,
      { type: ProjectType; accountId: string; folderId?: string | null; name?: string; subType?: ProjectSubtype | null }
    >({
      query: ({ type, subType, accountId, folderId, name }) => ({
        url: "/v2/projects",
        method: "POST",
        body: { FolderID: folderId ?? null, AccountID: accountId, Type: type, Subtype: subType ?? null, Name: name },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: "Project", id: `LIST:${arg.accountId}` }],
    }),
    createAttachmentThumbnails: build.mutation<Project, { projectId: string; attachmentIds?: string[] }>({
      query: ({ projectId, attachmentIds }) => ({
        url: `/v2/projects/${projectId}/generate-attachment-thumbnails`,
        method: "POST",
        body: { AttachmentIds: attachmentIds },
      }),
      invalidatesTags: (result, _error, _arg) => (result ? [{ type: "Project", id: `LIST:${result.Account.ID}` }] : []),
    }),
    updateProject: build.mutation<Project, { ID: string } & Partial<Project>>({
      query: ({ ID, ...updates }) => ({
        url: `/v2/projects/${ID}`,
        method: "PATCH",
        body: updates,
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Permission", id: arg.ID }],
      onQueryStarted: async ({ ID, ...updates }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          projectSlice.util.updateQueryData("getProject", ID, (draft) => {
            Object.assign(draft, updates);
          })
        );
        const projectsPatchResult = dispatch(
          projectSlice.util.updateQueryData("getProjects", undefined, (projects) => {
            const projectToUpdate = projects.entities[ID];
            if (projectToUpdate) {
              projectsAdapter.updateOne(projects, { id: ID, changes: updates });
            }
          })
        );
        queryFulfilled.catch(() => {
          patchResult.undo();
          projectsPatchResult.undo();
        });
      },
    }),
    deleteProject: build.mutation<OkResponse, Project | ProjectPreview>({
      query: ({ ID, ...project }) => ({
        url: `/v2/projects/${ID}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Project", id: `LIST:${arg.Account.ID}` },
        { type: "Project", id: arg.ID },
      ],
    }),
  }),
});

/**
 * Endpoint Hooks
 */
export const {
  useGetProjectQuery,
  useLazyGetProjectQuery,
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  useCopyProjectMutation,
  useCreateProjectMutation,
  useCreateAttachmentThumbnailsMutation,
  useDeleteProjectMutation,
  useUpdateProjectMutation,
} = projectSlice;

/**
 * Custom hook to fetch projects for the current account from the URL.
 * This hook automatically passes the account ID from the URL to the query.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useGetAccountProjects();
 * ```
 */
export const useGetAccountProjects = (): ReturnType<typeof useGetProjectsQuery> => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const accountId = currentAccount?.ID;
  return useGetProjectsQuery(accountId ? { accountId } : undefined, { skip: !accountId });
};

/**
 * Selectors
 */
const selectProjectsAccountId = (state: Store): string | undefined => {
  const currentAccount = selectCurrentAccount(state);
  return currentAccount?.ID;
};
const selectProjectsResult = createSelector([(state: Store) => state, selectProjectsAccountId], (state, accountId) => {
  return projectSlice.endpoints.getProjects.select(accountId ? { accountId } : undefined)(state);
});
const selectProjectsData = createSelector([selectProjectsResult], (projectsResult) => projectsResult?.data ?? projectsInitialState);

export const { selectAll: selectAllBaseProjects, selectById: selectProjectById } = projectsAdapter.getSelectors(selectProjectsData);

/**
 * Returns all projects (augmented with the workflow status).
 */
export const selectAllProjects = createDeepEqualSelector([selectAllBaseProjects, selectWorkflowEntities], (projects, workflows) => {
  return projects.map((project) => {
    const workflow = workflows?.[project.ID];
    return {
      ...project,
      // Tasks cannot be 'cancelled', but workflows can. We inject the workflow's cancel time
      // into the project status to allow the UI to distinguish between 'error' and 'cancelled' states.
      Status: workflow ? { ...workflow.Status, CancelTime: workflow.CancelTime ?? null } : null,
    };
  });
});

/**
 * Returns all projects with the current Account.
 */
export const selectProjectsWithinAccount = createDeepEqualSelector(
  [selectAllProjects, selectCurrentAccount],
  (projects, currentAccount): Project[] => {
    return projects.filter((project) => !currentAccount || project?.Account?.ID === currentAccount.ID);
  }
);

/**
 * Returns all planner projects within the current AccountID.
 */
export const selectPlannerProjectsWithinAccount = createDeepEqualSelector([selectProjectsWithinAccount], (projects): Project[] => {
  return projects.filter((project) => project.Type === "planner");
});

/**
 * Returns all projects with the specified ParentID
 */
export const selectProjectsByParentId = createDeepEqualSelector(
  [selectAllProjects, (_state: Store, parentId: string | null | undefined) => parentId],
  (projects, parentId): ProjectPreview[] => {
    return projects.filter((project) => (parentId === undefined && !project.FolderID) || project.FolderID === parentId);
  }
);

export const selectProjectsMatchingSearchKey = createDeepEqualSelector(
  [selectAllProjects, (_state: Store, searchKey: string) => searchKey],
  (projects, searchKey): ProjectPreview[] => {
    return searchCollectionByKey(projects, searchKey, "ProjectName");
  }
);

/*
 * Return the full account object relating to the given project.
 *
 * This will only succeed if the user is in the same account as the project, so care
 * should be taken when using this.
 */
export const selectProjectAccount = (state: Store, project: Pick<Project, "Account">): Account | undefined =>
  selectAccountById(state, project.Account.ID);
